import React, { useCallback, useEffect, useMemo } from 'react';
import { history, useDispatch, useIntl, useLocation, useParams } from 'umi';
import { Tabs } from 'tea-component';
import LeftMenu from '@/pages/home/pages/studio/components/StudioLeftMenu';
import TabContainer from '@/pages/home/pages/studio/tabContainer';
import { StudioContext } from '@/pages/home/pages/studio/context';
import {
  STUDIO_DEVELOPMENT,
  STUDIO_OPERATION,
} from '@/pages/home/pages/studio/constant';
import type { TStudioTab } from '@/pages/home/pages/studio/constant';
import {
  buildStudioGamePath,
  getStudioMenuPath,
  getStudioTabFromUrl,
  getTabChildPathFromRealPath,
  isDatabrainPage,
  isTabContainerActive,
  matchStudioMenuPath,
  matchStudioTabChildPath,
  parseStudioUrlInfo,
} from '@/pages/home/pages/studio/utils';
import { PAGE_TYPE_OUT_PATH, PAGE_TYPE_OUT_URL } from '@/pages/dashboard/_config/constant';
import { LANGUAGE_EN } from '@/pages/home/_config/config';
import { selectValByCondition } from '@home/utils/common';
import HeaderWrap from '../HeaderWrap';
import CasualHubChrome from '@/pages/home/pages/studio/casualHub/components/CasualHubChrome';
import { IStudioInfo } from '../../type';
import FullScreenBtn from '../FullScreenBtn';

interface IProps {
  children: React.ReactNode;
  studioInfo: IStudioInfo;
}
// game页面外层组件
const GameWrap = (props: IProps) => {
  const { studioInfo, children } = props;
  const location = useLocation();
  const { realPath } = parseStudioUrlInfo(location.pathname);
  const { studioId, gameCode } = useParams() || {} as any;
  const dispatch = useDispatch();
  const { locale } = useIntl();
  const activeTabInUrl = getStudioTabFromUrl(location.pathname, location.search);

  // currentGame选中游戏信息 currentMenu选中菜单信息
  const { currentGame, currentMenu, contextMenu, menuPermission, editPermission, companyInfo, hasResearchList, hasOperationList, activeDashboardList, activeTab } = useMemo(() => {
    const { game_list: gameList, has_studio_editor: studioEditor, company } = studioInfo?.data || {};
    // 根据路由gameCode匹配游戏信息，如果没有匹配到gameCode就默认为该工作室第一个游戏
    const currentGame =
      gameList?.find((game) =>
        game?.game_code
          ? game?.game_code === gameCode
          : game?.unified_edition_id === gameCode,
      ) || gameList?.[0];

    // 判断是否有研发专区数据
    const hasResearchList = (currentGame?.dashboard_research_list?.length ?? 0) > 0;
    // 判断是否有运营专区数据
    const hasOperationList = (currentGame?.dashboard_list?.length ?? 0) > 0;

    // 两个专区都有菜单时才显示 Segment 切换，只有一个专区有菜单时直接展示该专区
    let activeTab: TStudioTab = STUDIO_DEVELOPMENT;
    if (activeTabInUrl === STUDIO_DEVELOPMENT && hasResearchList) {
      activeTab = STUDIO_DEVELOPMENT;
    } else if (hasOperationList) {
      activeTab = STUDIO_OPERATION;
    }

    // 根据 activeTab 选择当前激活的菜单列表
    const activeDashboardList = activeTab === STUDIO_DEVELOPMENT && hasResearchList
      ? currentGame?.dashboard_research_list
      : currentGame?.dashboard_list;

    // 根据路由realPath匹配游戏选中菜单（在当前激活列表中匹配）
    // 先匹配普通菜单，再匹配 display_mode=tab 容器，避免 nameSlug 与普通菜单 path 相同时误匹配
    const findMenu = activeDashboardList?.find((item) => matchStudioMenuPath(item, realPath)) ?? activeDashboardList?.find((item) => {
      if (item.display_mode !== 'tab') return false;
      return isTabContainerActive(item.name_en, realPath);
    });
    // 公司信息
    const companyInfo = company;
    // 菜单权限
    const menuPermission = findMenu?.is_visit;
    // 编辑权限
    const editPermission = studioEditor;
    // 匹配到路径是外链的情况
    const isOut = findMenu?.page_type === PAGE_TYPE_OUT_PATH || findMenu?.page_type === PAGE_TYPE_OUT_URL;
    // 如果没有匹配到realPath和外链的菜单就默认为该游戏第一个有权限的页面
    const currentMenu = findMenu && !isOut ? findMenu : activeDashboardList?.[0];

    // display_mode=tab 容器：从 tabs 中找到当前激活的子 tab 作为 contextMenu
    // 子页组件（authPath/datalab/web 等）从 StudioContext 读 currentMenu 时拿到的是子 tab 数据
    let contextMenu = currentMenu;
    if (currentMenu?.display_mode === 'tab' && currentMenu?.tabs?.length) {
      const childPath = getTabChildPathFromRealPath(realPath);
      const activeChild = childPath
        ? currentMenu.tabs.find((t: any) => matchStudioTabChildPath(t, childPath))
        : null;
      contextMenu = activeChild ?? currentMenu.tabs[0] ?? currentMenu;
    }

    return { currentGame, currentMenu, contextMenu, menuPermission, editPermission, companyInfo, hasResearchList, hasOperationList, activeDashboardList, activeTab };
  }, [activeTabInUrl, gameCode, realPath, studioInfo?.data]);

  // Tab 切换回调（tea-component Tabs onActive）
  const onTabChange = useCallback((tab: { id: string; }) => {
    const value: TStudioTab = tab.id === STUDIO_DEVELOPMENT
      ? STUDIO_DEVELOPMENT
      : STUDIO_OPERATION;
    if (!currentGame) {
      return;
    }
    const targetList = value === STUDIO_DEVELOPMENT
      ? currentGame?.dashboard_research_list
      : currentGame?.dashboard_list;
    const firstMenuPath = getStudioMenuPath(targetList?.[0]);
    const code = currentGame?.game_code || currentGame?.unified_edition_id;
    if (firstMenuPath) {
      history.replace(
        buildStudioGamePath(
          studioId,
          code,
          firstMenuPath,
          value,
          hasResearchList,
        ),
      );
    }
  }, [currentGame, hasResearchList, studioId]);

  // Tabs 配置
  const tabItems = useMemo(() => [
    { id: STUDIO_OPERATION, label: selectValByCondition(locale === LANGUAGE_EN, 'Operations Hub', '运营专区') },
    { id: STUDIO_DEVELOPMENT, label: selectValByCondition(locale === LANGUAGE_EN, 'Development Hub', '研发专区') },
  ], [locale]);

  // 进入GameWrap 则代表是游戏类型页面
  useEffect(() => {
    dispatch({
      type: 'studio/setFrameType',
      payload: 'game',
    });
  }, []);

  // 路由没有游戏id和菜单路径需要重置路由
  useEffect(() => {
    if (!currentGame || !currentMenu || currentGame?.studio_code !== studioId) {
      return;
    }
    // 选中游戏和选中菜单信息跟路由对应不上重置路由操作
    const path = getStudioMenuPath(currentMenu);
    // 游戏code匹配错误
    const isCodeError = currentGame?.game_code
      ? currentGame?.game_code !== gameCode
      : currentGame?.unified_edition_id !== gameCode;

    if (!gameCode || isCodeError || !realPath || path !== realPath) {
      if (
        isDatabrainPage(currentMenu)
        && !isCodeError
        && gameCode
      ) {
        const code = currentGame?.game_code || currentGame?.unified_edition_id;
        return history.replace(
          buildStudioGamePath(
            studioId,
            code,
            path,
            activeTab as TStudioTab,
            hasResearchList,
          ),
        );
      }
      // display_mode=tab 容器：path 为 {nameSlug}/{childPath}，realPath 也是同格式
      // 若 realPath 已落在该容器下则无需整页重定向；但仅有 nameSlug、缺少子路径时需补全到首个子 tab
      // （顶部导航 first_game_dashboard_list 若未带 tabs 时可能只拼出 slug）
      if (currentMenu?.display_mode === 'tab' && isTabContainerActive(currentMenu.name_en, realPath) && !isCodeError && gameCode) {
        const tabHasChildPath = realPath.indexOf('/') >= 0;
        if (!tabHasChildPath && path && path !== realPath) {
          const code = currentGame?.game_code || currentGame?.unified_edition_id;
          return history.replace(
            buildStudioGamePath(
              studioId,
              code,
              path,
              activeTab as TStudioTab,
              hasResearchList,
            ),
          );
        }
        dispatch({
          type: 'studio/getExplainInfo',
          payload: {
            params: { studio_code: studioId, game_code: gameCode }
          },
        });
        return;
      }
      // 没有详细路径的情况,重定向到第一个dashboard
      const findMenuPath = getStudioMenuPath(
        activeDashboardList?.[0],
      );
      const code = currentGame?.game_code || currentGame?.unified_edition_id;
      return history.replace(
        buildStudioGamePath(
          studioId,
          code,
          findMenuPath,
          activeTab as TStudioTab,
          hasResearchList,
        ),
      );
    }
    // 指标注释
    dispatch({
      type: 'studio/getExplainInfo',
      payload: {
        params: { studio_code: studioId, game_code: gameCode }
      },
    });
  }, [activeDashboardList, activeTab, currentGame, currentMenu, gameCode, hasResearchList, realPath, studioId]);

  const isCasualStudio = studioId === 'casual';

  return <StudioContext.Provider value={{ currentGame, currentMenu: contextMenu, tabContainerMenu: currentMenu?.display_mode === 'tab' ? currentMenu : null, menuPermission, studioInfo: studioInfo?.data, editPermission, companyInfo, activeTab, activeDashboardList, onTabChange, hasResearchList }}>
    <HeaderWrap
      studioInfo={studioInfo}
      bgClassName={isCasualStudio ? 'dbh-studio-causal-background' : undefined}
      header={
        isCasualStudio
          ? <CasualHubChrome studioInfo={studioInfo} />
          : undefined
      }
    >
      {
        hasResearchList && hasOperationList && <div className="dbh-studio-context-tabs">
          <Tabs
            tabs={tabItems}
            activeId={activeTab}
            onActive={onTabChange}
          />
        </div>
      }
      <div className="dbh-studio-context-body">
        <LeftMenu studioInfo={studioInfo} />
        <div className="dbh-studio-context-right">
          <div className="dbh-studio-context-auto">
            {currentMenu?.display_mode === 'tab'
              ? <TabContainer>{children}</TabContainer>
              : (
                <FullScreenBtn
                  className="dbh-studio-game-page-fullscreen"
                  priority={0}
                >
                  {children}
                </FullScreenBtn>
              )
            }
          </div>
        </div>
      </div>
    </HeaderWrap>
  </StudioContext.Provider>;
};

export default GameWrap;
